import React, { useState, useEffect } from 'react';

import { Formik, Form } from 'formik';
import Yup from '../../utils/yup';
import dayjs from 'dayjs';

import { NotificationType } from '../../config';
import addNotification from '../../utils/addNotification';
import { useLoader } from '../../provider/LoaderProvider';
import useUsers from '../../context/UsersContext';
import { DeleteDialogUI, FormButtonPanelUI, FormHeaderUI } from '../Interface';
import _ from 'lodash';
import EmployeePlannerAbsenceFields from './EmployeePlannerAbsenceFields';
import employeePlanAbsenceService from '../../service/employeePlanAbsence.service';

export const EmployeePlannerAbsencePage = ({ employeePlanAbsenceState, dialog }) => {
  const {
    viewField,
    setViewField,
    selectedValues,
    setSelectedValues,
    operatorsDisabled,
    setOperatorsDisabled,
    canEdit,
    setRowSelectionModel,
    autocompleteOptions,
    setRefetchTable,
  } = employeePlanAbsenceState;
  const { getUser, user } = useUsers();
  const { showLoader, hideLoader } = useLoader();
  const [initValues, setInitValues] = useState({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [defaultValues, setDefaultValues] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});

  const def = {
    startDate: dayjs(new Date()),
    endDate: dayjs(new Date()),
    resourceOneId: null,
  };

  useEffect(() => {
    setDefaultValues(def);
    setDefaultSelectedValues(def);
    if (!Object.keys(user).length) getUser();
  }, []);

  const validationSchema = Yup.object().shape({
    startDate: Yup.string().required(),
    endDate: Yup.string().required(),
    resourceOneId: Yup.string().required(),
  });

  useEffect(() => {
    setSelectedValues(defaultSelectedValues);
  }, [defaultSelectedValues]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!_.isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    }
  }, [selectedValues]);

  useEffect(() => {
    if (dialog) {
      setViewField(true);
    }
  }, [dialog?.handleClose]);

  const valuesToForm = () => {
    const { id, startDate, endDate, resourceOneId } = selectedValues;

    setInitValues({
      id,
      startDate: dayjs(startDate),
      endDate: dayjs(endDate),
      resourceOneId,
    });
  };

  const handleInsertObject = (values) => {
    showLoader();
    const insertObject = {
      ...values,
      startDate: dayjs(values.startDate).startOf('day').add(12, 'hours').toISOString(),
      endDate: dayjs(values.endDate).startOf('day').add(12, 'hours').toISOString(),
      createdBy: user.userId,
    };

    employeePlanAbsenceService
      .createData(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
        setSelectedValues(data);
        setRowSelectionModel(data.id);

        if (dialog) {
          dialog.setDataChanged(true);
          dialog.handleClose();
        }
      })
      .finally(() => {
        hideLoader();
      });
  };

  const handleSubmit = (values) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField || !dialog.isUpdate) {
      handleInsertObject(values);
    }
  };

  const updateObject = async (values) => {
    const updData = {
      ...values,
      startDate: dayjs(values.startDate).startOf('day').add(12, 'hours').toISOString(),
      endDate: dayjs(values.endDate).startOf('day').add(12, 'hours').toISOString(),
      updatedBy: user.userId,
    };

    employeePlanAbsenceService.updateData(updData).then(() => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);

      if (dialog) {
        dialog.setDataChanged(true);
        dialog.handleClose();
      }
    });
  };

  const handleRemoveElement = async () => {
    const deleteData = {
      deletedBy: user.userId,
    };
    employeePlanAbsenceService.deleteData(deleteData, selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues(defaultSelectedValues);

      if (dialog) {
        dialog.setDataChanged(true);
        dialog.handleClose();
      }
    });
  };

  const onAddClick = () => {
    setSelectedValues(defaultSelectedValues);
    setInitValues(defaultValues);
    setViewField(false);
    setAddField(false);
  };

  const onModifyClick = () => {
    setViewField(false);
    setEditButtonvisible(false);
  };

  const onDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const onCopyClick = () => {
    setViewField(false);
    setAddField(false);
  };

  return (
    <>
      <div className="h-full overflow-auto custom-scrollbar">
        <FormHeaderUI
          addClick={!dialog ? onAddClick : null}
          addDisabled={!canEdit}
          modClick={(!dialog || dialog.isUpdate) && (!dialog || dialog.isUpdate ? onModifyClick : null)}
          modDisabled={!viewField || operatorsDisabled || !canEdit}
          deleteClick={(!dialog || dialog.isUpdate) && (!dialog || dialog.isUpdate ? onDeleteClick : null)}
          deleteDisabled={!viewField || operatorsDisabled || !canEdit}
          copyClick={(!dialog || dialog.isUpdate) && (!dialog || dialog.isUpdate ? onCopyClick : null)}
          copyDisabled={!viewField || operatorsDisabled || !canEdit}
        />

        <Formik
          initialValues={initValues}
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, resetForm, setFieldValue, validateField }) => (
            <Form>
              <EmployeePlannerAbsenceFields
                values={values}
                errors={errors}
                disabled={(!dialog || dialog?.isUpdate) && viewField}
                setFieldValue={setFieldValue}
                validateField={validateField}
                selectedValues={selectedValues}
                autocompleteOptions={autocompleteOptions}
              />

              <FormButtonPanelUI
                onCancelClick={() => {
                  setViewField(true);
                  setEditButtonvisible(true);
                  setAddField(true);
                  resetForm();
                  if (dialog) {
                    dialog.handleClose();
                  }
                }}
                disabled={
                  dialog
                    ? dialog.isUpdate && ((editButtonVisible && addField) || (!editButtonVisible && !addField))
                    : (editButtonVisible && addField) || (!editButtonVisible && !addField)
                }
              />
            </Form>
          )}
        </Formik>
      </div>
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};
